/* eslint-disable react-hooks/exhaustive-deps */
import DeleteIcon from '@mui/icons-material/Delete';
import AttachmentIcon from '@mui/icons-material/Attachment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    TextField, Typography, Box, Button, Paper,
    Grid, IconButton, Snackbar, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tooltip, Autocomplete
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import MuiAlert from '@mui/material/Alert';
import DocumentPicker from './DocumentPicker';
import { NPA_ACCOUNT_OPTIONS } from './AccountsTab';

const FINAL_ACCOUNT_OPTIONS = NPA_ACCOUNT_OPTIONS.filter(opt => opt.label !== 'DRAFT ACCOUNTS');

const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    try {
        return date.toISOString().split('T')[0];
    } catch (e) {
        return '';
    }
};

// Reusable "link a document" button that opens the shared DocumentPicker.
const AttachButton = ({ label, attached, onClick }) => (
    <Button
        fullWidth
        variant="outlined"
        startIcon={<AttachmentIcon />}
        onClick={onClick}
        color={attached ? 'success' : 'primary'}
    >
        {attached ? `${label} Attached` : `Attach ${label}`}
    </Button>
);

const NPAAccountsTab = ({ matter, mode }) => {
    const isDraft = mode === 'draft';
    const legalMatterId = matter?.id || '';
    const theme = useTheme();

    const emptyFormData = () => (
        isDraft
            ? {
                accountType: 'DRAFT ACCOUNTS',
                npaDraftedDate: '',
                npaSubmittedWithReportDate: '',
                ldAccountDocumentUrl: null,
                npaReportDocumentUrl: null,
                legalMatterId,
                draftedBy: '',
            }
            : {
                accountType: '',
                npaDraftedDate: '',
                npaSubmittedToNDPPDate: '',
                npaApprovedByNDPPDate: '',
                npaSubmittedToMasterDate: '',
                npaApprovedByMasterDate: '',
                ldAccountDocumentUrl: null,
                legalMatterId,
                draftedBy: '',
            }
    );

    const [tableData, setTableData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState(emptyFormData());
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [user, setUser] = useState(null);

    // 'account' | 'report' — which field the DocumentPicker is currently editing
    const [docPickerOpen, setDocPickerOpen] = useState(false);
    const [docPickerTarget, setDocPickerTarget] = useState('account');
    const [isPickerOpening, setIsPickerOpening] = useState(false);

    const normalize = (path) => path ? path.replace(/\\/g, '/').toLowerCase() : "";

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await axios.get('/api/user', { withCredentials: true });
                if (typeof res.data !== 'object' || res.data === null) { setUser(null); return; }
                if (!res.data.role) res.data.role = 'guest';
                setUser(res.data);
            } catch (err) {
                setUser(null);
            }
        };
        fetchUser();
    }, []);

    const fetchAccounts = async () => {
        if (!legalMatterId) return;
        try {
            const response = await axios.get(`/api/accounts?legalMatterId=${legalMatterId}`);
            const accountsArray = response.data?.data ?? response.data ?? [];
            const filtered = (Array.isArray(accountsArray) ? accountsArray : []).filter(row => {
                const type = String(row.accountType || '').toUpperCase();
                return isDraft ? type === 'DRAFT ACCOUNTS' : type !== 'DRAFT ACCOUNTS';
            });
            setTableData(filtered);
        } catch (error) {
            setSnackbarMessage('Error fetching accounts.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, [legalMatterId, mode]);

    // Ensures the DocumentPicker opens after formData has been updated by handleEdit.
    useEffect(() => {
        if (isPickerOpening) {
            setDocPickerOpen(true);
            setIsPickerOpening(false);
        }
    }, [isPickerOpening]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData(emptyFormData());
        setSelectedRow(null);
    };

    const handleAdd = async () => {
        if (!legalMatterId) {
            setSnackbarMessage('Missing matter reference.');
            setSnackbarSeverity('warning');
            setOpenSnackbar(true);
            return;
        }
        if (!isDraft && !formData.accountType) {
            setSnackbarMessage('Please select an Account Type.');
            setSnackbarSeverity('warning');
            setOpenSnackbar(true);
            return;
        }

        const newAccount = { ...formData, legalMatterId, draftedBy: formData.draftedBy || '' };

        try {
            const response = await axios.post('/api/accounts', newAccount);
            setTableData([...tableData, response.data]);
            setSnackbarMessage('Account added successfully.');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            resetForm();
        } catch (error) {
            setSnackbarMessage('Error adding account.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleEdit = (row) => {
        setSelectedRow(row);
        setFormData({ ...emptyFormData(), ...row, legalMatterId, draftedBy: row.draftedBy || '' });
    };

    const handleSave = async () => {
        if (!selectedRow) return;
        try {
            await axios.put(`/api/accounts/${selectedRow.id}`, { ...formData, legalMatterId, draftedBy: formData.draftedBy || '' });
            fetchAccounts();
            setSnackbarMessage('Account updated successfully.');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            resetForm();
        } catch (error) {
            setSnackbarMessage('Error updating account.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleDelete = async () => {
        if (selectedRow && window.confirm('Are you sure you want to delete this account record?')) {
            try {
                await axios.delete(`/api/accounts/${selectedRow.id}`);
                setTableData(tableData.filter(row => row.id !== selectedRow.id));
                resetForm();
                setSnackbarMessage('Account deleted successfully.');
                setSnackbarSeverity('success');
                setOpenSnackbar(true);
            } catch (error) {
                setSnackbarMessage('Error deleting account.');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            }
        }
    };

    const handleAction = () => selectedRow ? handleSave() : handleAdd();
    const handleCancel = () => resetForm();
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };

    const openPicker = (target) => {
        setDocPickerTarget(target);
        setDocPickerOpen(true);
    };

    const activeDocField = docPickerTarget === 'account' ? 'ldAccountDocumentUrl' : 'npaReportDocumentUrl';

    const title = isDraft ? 'Draft Accounts' : 'Final Accounts';
    const tableHeaders = isDraft
        ? ['Docs', 'Date Drafted', 'Date Submitted with Report']
        : ['Docs', 'Account Type', 'Date Drafted', 'Submitted NDPP', 'Approved NDPP', 'Submitted Master', 'Approved Master'];

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" align="center" sx={{ color: 'text.primary' }}>{title} Management</Typography>
            </Grid>

            {/* Entry Form */}
            <Grid item xs={12}>
                <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {selectedRow ? `Editing ${title} ID: ${selectedRow.id}` : `Add New ${title.slice(0, -1)} Record`}
                    </Typography>
                    <Grid container spacing={2}>
                        {!isDraft && (
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={FINAL_ACCOUNT_OPTIONS.map(opt => opt.label)}
                                    value={formData.accountType || ''}
                                    onChange={(e, newValue) => handleChange({ target: { name: 'accountType', value: newValue || '' } })}
                                    disabled={user?.role === 'guest'}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Account Type*" variant="outlined" fullWidth />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} sm={isDraft ? 6 : 6}>
                            <TextField
                                fullWidth
                                label="Date Account Drafted"
                                name="npaDraftedDate"
                                type="date"
                                value={formatDateForInput(formData.npaDraftedDate)}
                                variant="outlined"
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                disabled={user?.role === 'guest'}
                                inputProps={{ max: '9999-12-31' }}
                            />
                        </Grid>

                        {isDraft ? (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Date Submitted with Report"
                                    name="npaSubmittedWithReportDate"
                                    type="date"
                                    value={formatDateForInput(formData.npaSubmittedWithReportDate)}
                                    variant="outlined"
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={user?.role === 'guest'}
                                    inputProps={{ max: '9999-12-31' }}
                                />
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date Submitted to NDPP"
                                        name="npaSubmittedToNDPPDate"
                                        type="date"
                                        value={formatDateForInput(formData.npaSubmittedToNDPPDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest'}
                                        inputProps={{ max: '9999-12-31' }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date Approved by NDPP"
                                        name="npaApprovedByNDPPDate"
                                        type="date"
                                        value={formatDateForInput(formData.npaApprovedByNDPPDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest'}
                                        inputProps={{ max: '9999-12-31' }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date Submitted to Master"
                                        name="npaSubmittedToMasterDate"
                                        type="date"
                                        value={formatDateForInput(formData.npaSubmittedToMasterDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest'}
                                        inputProps={{ max: '9999-12-31' }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date Approved by Master"
                                        name="npaApprovedByMasterDate"
                                        type="date"
                                        value={formatDateForInput(formData.npaApprovedByMasterDate)}
                                        variant="outlined"
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={user?.role === 'guest'}
                                        inputProps={{ max: '9999-12-31' }}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={isDraft ? 6 : 12}>
                            <AttachButton
                                label="Account Document"
                                attached={!!formData.ldAccountDocumentUrl}
                                onClick={() => openPicker('account')}
                            />
                        </Grid>
                        {isDraft && (
                            <Grid item xs={12} sm={6}>
                                <AttachButton
                                    label="Report Document"
                                    attached={!!formData.npaReportDocumentUrl}
                                    onClick={() => openPicker('report')}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start" gap={2} sx={{ mt: 1 }}>
                    <Button variant="contained" color="primary" onClick={handleAction} disabled={user?.role === 'guest'} size="small">
                        {selectedRow ? `Update ${title.slice(0, -1)}` : `Add New ${title.slice(0, -1)}`}
                    </Button>
                    <Button variant="outlined" onClick={handleCancel} disabled={user?.role === 'guest'} size="small">
                        Reset / Cancel Edit
                    </Button>
                    {selectedRow && (
                        <Button variant="outlined" color="error" onClick={handleDelete} disabled={user?.role === 'guest'} startIcon={<DeleteIcon />} size="small">
                            Delete
                        </Button>
                    )}
                </Box>
            </Grid>

            {/* Table */}
            <Grid item xs={12}>
                <Paper elevation={3} sx={{ padding: 2, borderRadius: 2, bgcolor: theme.palette.background.paper, overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                    {tableHeaders.map((header) => (
                                        <TableCell key={header} sx={{ color: theme.palette.text.secondary, fontWeight: 'bold', minWidth: header.includes('Date') || header.includes('Drafted') || header.includes('NDPP') || header.includes('Master') ? 130 : 150 }}>
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={tableHeaders.length} align="center" sx={{ color: theme.palette.text.secondary, py: 3 }}>
                                            No {title.toLowerCase()} available.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    tableData.map((row, index) => (
                                        <TableRow
                                            key={row.id || index}
                                            onClick={() => handleEdit(row)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: selectedRow?.id === row.id ? theme.palette.action.selected : (index % 2 === 0 ? theme.palette.background.paper : theme.palette.grey[50]),
                                                '&:hover': { bgcolor: theme.palette.action.hover },
                                            }}
                                        >
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Tooltip title="View Account Document">
                                                        <IconButton size="small" disabled={!row.ldAccountDocumentUrl} onClick={(e) => { e.stopPropagation(); handleEdit(row); setDocPickerTarget('account'); setIsPickerOpening(true); }}>
                                                            <AttachmentIcon fontSize="inherit" color={row.ldAccountDocumentUrl ? 'primary' : 'disabled'} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {isDraft && (
                                                        <Tooltip title="View Report Document">
                                                            <IconButton size="small" disabled={!row.npaReportDocumentUrl} onClick={(e) => { e.stopPropagation(); handleEdit(row); setDocPickerTarget('report'); setIsPickerOpening(true); }}>
                                                                <VisibilityIcon fontSize="inherit" color={row.npaReportDocumentUrl ? 'secondary' : 'disabled'} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            {isDraft ? (
                                                <>
                                                    <TableCell>{formatDateForInput(row.npaDraftedDate)}</TableCell>
                                                    <TableCell>{formatDateForInput(row.npaSubmittedWithReportDate)}</TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell>{row.accountType || ''}</TableCell>
                                                    <TableCell>{formatDateForInput(row.npaDraftedDate)}</TableCell>
                                                    <TableCell>{formatDateForInput(row.npaSubmittedToNDPPDate)}</TableCell>
                                                    <TableCell>{formatDateForInput(row.npaApprovedByNDPPDate)}</TableCell>
                                                    <TableCell>{formatDateForInput(row.npaSubmittedToMasterDate)}</TableCell>
                                                    <TableCell>{formatDateForInput(row.npaApprovedByMasterDate)}</TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>

            <DocumentPicker
                open={docPickerOpen}
                onClose={() => setDocPickerOpen(false)}
                matterId={legalMatterId}
                user={user}
                onDocumentSelect={(filepath) => {
                    setFormData(prev => ({ ...prev, [activeDocField]: filepath }));
                    setDocPickerOpen(false);
                }}
                onDocumentDelete={(deletedFilepath) => {
                    if (normalize(formData.ldAccountDocumentUrl) === normalize(deletedFilepath)) {
                        setFormData(prev => ({ ...prev, ldAccountDocumentUrl: null }));
                    }
                    if (normalize(formData.npaReportDocumentUrl) === normalize(deletedFilepath)) {
                        setFormData(prev => ({ ...prev, npaReportDocumentUrl: null }));
                    }
                }}
                folder={title}
                activeDocPath={formData[activeDocField]}
            />

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
        </Grid>
    );
};

export default NPAAccountsTab;
